import type { CostAnalyticsPayload } from "../../types/analytics.types";
import type {
  ParentCustomerRequest,
  RawCostReportRequest,
  ServiceFilterRequest,
} from "../../types/cost-report.types";
import type {
  CostDashboardFiltersPayload,
  CostPayload,
} from "../../types/summary.types";

/**
 * Build a stable cache key for a RawCostReportRequest payload.
 * Normalizes ordering of array fields and stamp casing so semantically-equal payloads
 * produce identical keys.
 */
export const buildRawCostUsagePayloadKey = (
  payload: RawCostReportRequest,
): string => {
  const normalized = {
    ...payload,
    reportType: payload.reportType,
    stamp: payload.stamp?.toUpperCase(),
    subscriptions: [...payload.subscriptions].sort(),
    customers: [...payload.customers].sort(),
    serviceNames: [...payload.serviceNames].sort(),
    meterSubcategories: [...payload.meterSubcategories].sort(),
  };

  return JSON.stringify(normalized);
};

/**
 * Build a stable key for ServiceFilterRequest payloads used to fetch filters by category.
 */
export const buildServiceFiltersByCategoryPayloadKey = (
  payload: ServiceFilterRequest,
): string => {
  const normalized = {
    ...payload,
    reportType: payload.reportType,
    stamp: payload.stamp?.toUpperCase(),
    customers: [...payload.customers].sort(),
    subscriptions: [...payload.subscriptions].sort(),
  };

  return JSON.stringify(normalized);
};

/**
 * Build a key for parent-customer lookup payloads.
 */
export const buildParentCustomersPayloadKey = (
  payload: ParentCustomerRequest,
): string => {
  const normalized = {
    subscriptionIds: [...payload.subscriptionIds].sort(),
  };
  return JSON.stringify(normalized);
};

/**
 * Build a key for dashboard payloads (used for caching or tracking requests).
 */
export const buildDashboardPayloadKey = (payload: CostPayload): string => {
  const normalized = {
    ...payload,
    subscriptions: [...payload.subscriptions].sort(),
    customers: [...payload.customers].sort(),
    resourceGroups: [...payload.resourceGroups].sort(),
  };
  return JSON.stringify(normalized);
};

/**
 * Build a predictable key for dashboard filters payloads.
 */
export const buildFiltersPayloadKey = (
  payload: CostDashboardFiltersPayload,
): string =>
  JSON.stringify({ subscriptionIds: [...payload.subscriptionIds].sort() });

/**
 * Build a key for analytics payloads, normalizing array order so equivalent payloads
 * produce identical keys.
 */
export const buildAnalyticsPayloadKey = (
  payload: CostAnalyticsPayload,
): string => {
  const normalized = {
    ...payload,
    subscriptions: [...payload.subscriptions].sort(),
    customers: [...payload.customers].sort(),
    resourceGroups: [...payload.resourceGroups].sort(),
    services: [...payload.services].sort(),
  };
  return JSON.stringify(normalized);
};
